import type {
  UseMutationOptions,
  UseMutationResult,
} from '@tanstack/react-query'
import { useMutation } from '@tanstack/react-query'

import { QUERIES } from '@/enums/queries'
import type { Packet, PatialPacket } from '@/types/packet'

import { updatePacket } from './services/mutatePackets'
import { invalidateLoadPacket, invalidateLoadPackets } from './useLoadPackets'

type Props = {
  updatePacket: (id: string, data: PatialPacket) => Promise<Packet>
  isLoading: boolean
} & UseMutationResult<Packet, Error, UpdateProps>

type UpdateProps = { id: string; data: PatialPacket }

export const useUpdatePackets = (
  { invalidateAll = true }: { invalidateAll?: boolean } = {},
  options: Omit<
    UseMutationOptions<Packet, Error, UpdateProps>,
    'mutationKey' | 'mutationFn'
  > = {}
): Props => {
  const mutation = useMutation({
    mutationKey: [QUERIES.PACKETS.UPDATE],
    mutationFn: async ({ id, data }: UpdateProps) =>
      await updatePacket(id, data),
    onSuccess: async () => {
      if (invalidateAll) {
        await invalidateLoadPackets()
      } else {
        await invalidateLoadPacket()
      }
    },
    ...options,
  })

  const updatePacketReq = async (id: string, data: PatialPacket) => {
    return mutation.mutateAsync({ id, data })
  }

  return {
    updatePacket: updatePacketReq,
    ...mutation,
  }
}
